/**
 * practicesAPI (HA-320).
 *
 * Patient-scoped wrapper around /api/v1/me/practices. Returns the list of
 * practices the calling patient has an active client-practitioner
 * relationship with — backs the header PracticeSwitcher. Identity comes
 * from the JWT; no clientId in the URL.
 */

import axios from 'axios';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';

/** One practice the caller is actively connected to. Mirrors the backend
 *  PracticeContextDto (Healio.Dto.Practice). */
export interface PracticeContextDto {
  /** Canonical practice id — the value passed back as ?practice_id=. */
  practiceId: string;
  /** Human-readable practice name for the switcher dropdown. */
  displayName: string;
  /** True for the practice that matches the caller's JWT primary. */
  isPrimary: boolean;
  /** ISO timestamp of when the relationship was established. */
  connectedSince: string;
}

const practicesApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/v1/me/practices`,
  withCredentials: true,
});

installSessionInterceptors(practicesApi);

/**
 * GET /api/v1/me/practices — list the calling patient's active practices,
 * sorted primary-first. Returns an empty array when the caller has no
 * active relationships (e.g. a newly-invited patient).
 */
export const getActivePractices = async (): Promise<PracticeContextDto[]> => {
  const response = await practicesApi.get<PracticeContextDto[]>('');

  return response.data ?? [];
};
